/**
 * Fichero multimedia de un mensaje: imagen, audio, video o documento.
 *
 * El panel pide el fichero a la propia API (`/api/media/...`) y no a Meta: la URL
 * de Meta exige el token de acceso y caduca, asi que no se puede enlazar desde el
 * navegador. La API lo descarga la primera vez y lo sirve desde su cache.
 *
 * Si el navegador no puede mostrarlo (por ejemplo un audio antiguo que WhatsApp
 * ya no conserva), se ensena una explicacion y el enlace de descarga: un icono de
 * reproductor roto no dice nada al agente.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { mediaUrl } from '../api.ts';
import { Icon } from './Icon.tsx';

type MediaState = 'idle' | 'loading' | 'ready' | 'error';

export function MediaAttachment({
  messageId,
  kind,
  media,
  textColor,
}: {
  messageId: string;
  /** Tipo del mensaje de WhatsApp: decide el reproductor que se pinta. */
  kind: 'image' | 'audio' | 'video' | 'document' | 'sticker';
  media: { id?: string; mimeType?: string; filename?: string; caption?: string };
  /** Color de texto del boton de reintento (dentro de la burbuja). */
  textColor: string;
}): ReactElement {
  const [state, setState] = useState<MediaState>('loading');
  const [attempt, setAttempt] = useState(0);

  // El nombre del fichero es el de la URL que genera el backend: el que envio el
  // cliente si existe y, si no, `<tipo>.<extension>`.
  const displayName = media.filename ?? defaultName(kind, media.mimeType);
  const src = mediaUrl(messageId, displayName);

  const retry = (): void => {
    setState('loading');
    setAttempt((value) => value + 1);
  };

  if (!src) {
    return (
      <MediaError
        textColor={textColor}
        message="No hay sesion iniciada, asi que no se puede pedir el fichero."
        onRetry={null}
      />
    );
  }

  if (kind === 'image' || kind === 'sticker') {
    return (
      <figure className={kind === 'sticker' ? 'media media--sticker' : 'media'}>
        {state === 'loading' ? <span className="media__placeholder" aria-hidden="true" /> : null}
        {state === 'error' ? (
          <MediaError
            textColor={textColor}
            message="No se pudo mostrar la imagen. Puede que WhatsApp ya no conserve el fichero (solo lo guarda unos 30 dias)."
            onRetry={retry}
          />
        ) : (
          <a
            className="media__image-link"
            href={src}
            target="_blank"
            rel="noreferrer"
            title="Abrir la imagen a tamano completo"
          >
            <img
              key={attempt}
              className="media__image"
              src={src}
              alt={media.caption ?? 'Imagen enviada por el cliente'}
              loading="lazy"
              onLoad={() => setState('ready')}
              onError={() => setState('error')}
            />
          </a>
        )}
      </figure>
    );
  }

  if (kind === 'audio') {
    return (
      <div className="media">
        {state === 'error' ? (
          <MediaError
            textColor={textColor}
            message="No se pudo cargar el audio. Puede que WhatsApp ya no conserve el fichero (solo lo guarda unos 30 dias)."
            onRetry={retry}
          />
        ) : (
          <audio
            key={attempt}
            className="media__audio"
            src={src}
            controls
            preload="metadata"
            onLoadedMetadata={() => setState('ready')}
            onError={() => setState('error')}
          >
            Tu navegador no puede reproducir este audio. Usa el enlace de descarga.
          </audio>
        )}
      </div>
    );
  }

  if (kind === 'video') {
    return (
      <div className="media">
        {state === 'error' ? (
          <MediaError
            textColor={textColor}
            message="No se pudo cargar el video. Puede que WhatsApp ya no conserve el fichero (solo lo guarda unos 30 dias)."
            onRetry={retry}
          />
        ) : (
          <video
            key={attempt}
            className="media__video"
            src={src}
            controls
            preload="metadata"
            onLoadedMetadata={() => setState('ready')}
            onError={() => setState('error')}
          >
            Tu navegador no puede reproducir este video. Usa el enlace de descarga.
          </video>
        )}
      </div>
    );
  }

  // Documento: se ofrece la descarga, no se intenta previsualizar.
  return (
    <div className="media">
      <a className="media__file" href={src} download={displayName} rel="noreferrer">
        <Icon name="document" className="media__file-icon" />
        <span className="media__file-text">
          <span className="media__file-name">{displayName}</span>
          <span className="media__file-hint">
            {media.mimeType ? `${media.mimeType} · ` : ''}Descargar
          </span>
        </span>
        <Icon name="download" className="media__file-action" />
      </a>
    </div>
  );
}

function MediaError({
  message,
  onRetry,
  textColor,
}: {
  message: string;
  onRetry: (() => void) | null;
  textColor: string;
}): ReactElement {
  return (
    <div className="media__error" role="status">
      <Icon name="warning" className="media__error-icon" />
      <span className="media__error-text">{message}</span>
      {onRetry ? (
        <button type="button" className="media__retry" style={{ color: textColor }} onClick={onRetry}>
          Reintentar
        </button>
      ) : null}
    </div>
  );
}

/** Nombre con el que se sirve el fichero cuando el cliente no mando uno. */
function defaultName(kind: string, mimeType: string | undefined): string {
  const extension = extensionFor(mimeType);
  return `${kind}.${extension}`;
}

/** Extension coherente con el tipo MIME, la misma que usa el backend. */
function extensionFor(mimeType: string | undefined): string {
  const base = mimeType?.split(';')[0]?.trim().toLowerCase();
  const known: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'audio/ogg': 'ogg',
    'audio/opus': 'opus',
    'audio/mpeg': 'mp3',
    'audio/mp4': 'm4a',
    'video/mp4': 'mp4',
    'application/pdf': 'pdf',
  };
  if (base && known[base]) return known[base];
  const subtype = base?.split('/')[1]?.replace(/[^a-z0-9]/g, '');
  return subtype && subtype.length <= 5 ? subtype : 'bin';
}
